import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useState, useEffect, useCallback } from 'react';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { IMessage } from '~/types/campaigns';
import { BsPin } from 'react-icons/bs';
import { LuListTodo } from 'react-icons/lu';
import { MdOutlineStickyNote2 } from 'react-icons/md';
import { IoCheckmarkOutline } from 'react-icons/io5';
import { FiEye } from 'react-icons/fi';
import { RiDeleteBinLine } from 'react-icons/ri';
import moment from 'moment';
import ViewMessageModal from '~/components/Messages/modals/ViewMessage';
import AddMessageModal from '~/components/Messages/modals/AddMessage';
import DeleteMessageModal from '~/components/Messages/modals/DeleteMessage';
import { useQuery } from '@tanstack/react-query';
import {
    deleteCampaignRequestNote,
    deleteMessage,
    fetchAssignees,
    fetchMessages,
    updateCampaignRequestNote,
    updateMessage,
} from '~/services/campaign';
import Check from '~/components/Check/Check';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import Select from 'react-select';
import { Pagination } from '~/components/Pagination';
import { FaSearch } from 'react-icons/fa';
import { colorStyles } from '~/common/SelectStyles';
import { useAuth } from '~/context/AuthProvider';

const columnHelper = createColumnHelper<IMessage & { action?: string }>();

const columns = [
    columnHelper.accessor('isPinned', {
        header: () => <BsPin size={24} />,
        cell: (props) => {
            return props.getValue() ? (
                <BsPin size={24} className='text-red' />
            ) : (
                <></>
            );
        },
    }),
    columnHelper.accessor('type', {
        header: () => 'Type',
        cell: (props) => {
            const isCompleted = props?.row.original?.completed_date;
            if (isCompleted) return <Check />;
            switch (props.getValue()) {
                case 'TODO':
                    return <LuListTodo size={24} className='text-primary' />;
                case 'NOTE':
                    return (
                        <MdOutlineStickyNote2
                            size={24}
                            className='text-primary'
                        />
                    );
            }
        },
    }),
    columnHelper.accessor('date', {
        header: () => 'Date',
        cell: (props) => {
            const date = props.getValue();
            const formattedDate = moment(date).format('MM/DD/YYYY');
            const formattedTime = moment(date).format('hh:mm A');

            return (
                <div>
                    <span>{formattedDate}</span>
                    <br />
                    <span className='text-xs text-secondary'>
                        {formattedTime}
                    </span>
                </div>
            );
        },
    }),
    columnHelper.accessor('assignee', {
        header: () => 'For',
        cell: (props) => props.renderValue(),
    }),
    columnHelper.accessor('campaign', {
        header: () => 'Campaign',
        cell: (props) => {
            const campaign_id = props.row.original.campaign_id;
            const campaign_request_id = props.row.original.campaign_request_id;

            const linkTo = campaign_id
                ? `/campaigns/${campaign_id}`
                : campaign_request_id
                ? `/campaign-requests/${campaign_request_id}`
                : '#';

            return (
                <Link to={linkTo} className='text-blue-500 text-primary'>
                    {props.renderValue()}
                </Link>
            );
        },
    }),
    columnHelper.accessor('message', {
        header: () => 'Message',
        cell: (props) => (
            <span dangerouslySetInnerHTML={{ __html: props.getValue() }}></span>
        ),
    }),
];

enum Sort {
    ASC = 'asc',
    DESC = 'desc',
}

type DateRange = {
    start_date: string;
    end_date: string;
};

const initialDateRange = {
    start_date: '',
    end_date: '',
};

type Message = {
    id: number;
    todo_flag: boolean;
    created_date: string;
    pinned_flag: boolean;
    campaign_name: string;
    patient_name: string;
    note: string;
    created_by_name: string;
    completed_by_name: string;
    completed_date: string | null;
    campaign: number;
    assignee: string;
};

const pageSize = 20;
const Messages: React.FC = () => {
    const { user, person } = useAuth();
    const isPsc = user?.roles?.includes('patient services coordinator');
    // Dept Messages opens on the viewer's own department. The server picks
    // which one (a person can be in several) and sends it on the person
    // payload, so this only displays it - see
    // new_admin.utils.dept_messages_department_for_person (HHLTB-2688)
    const deptMessagesDepartment =
        person?.dept_messages_department || 'your department';
    const [pages, setPages] = useState<number>(0);
    const [sort, setSort] = useState<Sort | null>(null);
    const [sortBy, setSortBy] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
    const [page, setPage] = useState(1);
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [only_todos, setOnlyShowChecked] = useState<boolean>(false);
    const [campaignName, setCampaignName] = useState('');
    const [assignee, setAssignee] = useState<{
        label: string;
        value: string;
    } | null>(null);
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state as {
        todo?: boolean;
        deptMessages?: boolean;
        myMessages?: boolean;
    };
    const [deptMessages, setDeptMessages] = useState(
        state?.deptMessages || false,
    );
    const [myMessages, setMyMessages] = useState(state?.myMessages || false);
    const [hideDeleted, setHideDeleted] = useState(true);

    useEffect(() => {
        setDeptMessages(state?.deptMessages || false);
    }, [state?.deptMessages]);

    useEffect(() => {
        setMyMessages(state?.myMessages || false);
    }, [state?.myMessages]);

    // For PSCs, show their name as the default placeholder in the assignee dropdown.
    // PSCs aren't in the assignee list (they're not staff), so we use my_campaigns
    // filtering on the backend instead of an assignee ID.
    const defaultAssigneePlaceholder =
        isPsc && person
            ? `${person.first_name} ${person.last_name}`
            : 'Assignee';

    const clearDashboardFilter = () => {
        setDeptMessages(false);
        setMyMessages(false);
        setOnlyShowChecked(false);
        setAssignee(null);
        navigate(location.pathname, { replace: true, state: {} });
    };

    const { data, refetch, isLoading, isRefetching } = useQuery({
        queryKey: [
            'campaigns',
            { sort, sortBy, page },
            dateRange,
            assignee?.value,
            only_todos,
            isPsc,
            deptMessages,
            myMessages,
            hideDeleted,
        ],
        queryFn: () =>
            fetchMessages(
                {
                    sort,
                    sortBy,
                    only_todos,
                    hide_deleted: hideDeleted,
                    ...(deptMessages ? { dept_messages: true } : {}),
                    ...(myMessages && !assignee ? { my_messages: true } : {}),
                    ...(isPsc && !assignee && !deptMessages && !myMessages
                        ? only_todos
                            ? { my_todos: true }
                            : { my_campaigns: true }
                        : {}),
                },
                dateRange,
                page,
                campaignName,
                assignee?.value,
            ),
        staleTime: Infinity,
    });

    useEffect(() => {
        setOnlyShowChecked(state?.todo || false);
    }, [state?.todo]);

    useEffect(() => {
        if (data) {
            setMessages(
                data?.results?.map((item: any) => {
                    return {
                        id: item?.id,
                        campaign_id: item?.campaign,
                        campaign_request_id: item?.campaign_request_id,
                        type: item?.todo_flag ? 'TODO' : 'NOTE',
                        date: item?.created_date,
                        isPinned: item?.pinned_flag,
                        campaign: item?.patient_name || item?.campaign_name,
                        message: item?.note,
                        created_by_name: item?.created_by_name,
                        completed_by_name: item?.completed_by_name,
                        completed_date: item?.completed_date,
                        assignee: item?.assignee,
                    };
                }),
            );
        }
    }, [data]);

    useEffect(() => {
        if (data) {
            const total = data?.count;
            if (total) {
                setPages(Math.ceil(total / pageSize));
            }
        }
    }, [data]);

    const table = useReactTable<IMessage>({
        data: messages,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleSort = (columnId: string) => {
        if (sortBy === columnId) {
            setSort(sort === Sort.ASC ? Sort.DESC : Sort.ASC);
        } else {
            setSortBy(columnId);
            setSort(Sort.ASC);
        }
    };

    const handleDelete = async (
        id: number,
        campaignRequestId?: number | null,
    ) => {
        try {
            // Campaign request notes live in their own table, so an id from
            // that source would hit an unrelated Message if sent to the
            // messages endpoint.
            const res = campaignRequestId
                ? await deleteCampaignRequestNote(campaignRequestId, id)
                : await deleteMessage(id);
            if (res.ok) {
                refetch();
            }
        } catch (error) {
            console.error('Failed to delete message:', error);
        }
    };

    const handleUpdateMessage = async (
        id: number,
        payload: any,
        campaignRequestId?: number | null,
    ) => {
        try {
            const res = campaignRequestId
                ? await updateCampaignRequestNote(campaignRequestId, id, payload)
                : await updateMessage(id, payload);
            if (res.ok) {
                refetch();
            }
        } catch (error) {
            console.error('Failed to update message:', error);
        }
    };

    useEffect(() => {
        refetch();
    }, [only_todos]);

    const handleOnlyShowChecked = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setOnlyShowChecked(event.target.checked);
    };

    const debouncedRefetch = useCallback(
        debounce(async () => {
            await refetch();
        }, 1000),
        [refetch],
    );

    const filterCampaigns = (value: string) => {
        setCampaignName(value);
        debouncedRefetch();
    };

    const { data: assigneeOptions } = useQuery({
        queryKey: ['assignees'],
        queryFn: async () => {
            const assignees = await fetchAssignees();
            return assignees?.flatMap((group: any[]) => {
                const groupMembers = group[1];
                return groupMembers.map((member: any) => ({
                    value: member[0],
                    label: member[1],
                }));
            });
        },
        staleTime: Infinity,
    });

    const handleForFilter = (
        value: { label: string; value: string } | null,
    ) => {
        setAssignee(value);
    };

    return (
        <>
            <Breadcrumb pageName='Messages' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex items-center justify-between gap-4 px-6 pb-4'>
                    <div className='w-full min-w-[175px]'>
                        <Select
                            menuPlacement='auto'
                            placeholder={
                                deptMessages
                                    ? `${deptMessagesDepartment} (Dept)`
                                    : defaultAssigneePlaceholder
                            }
                            options={assigneeOptions || []}
                            className='w-full'
                            isClearable
                            onChange={(value) => {
                                handleForFilter(value);
                            }}
                            value={assignee}
                            styles={colorStyles}
                        />
                    </div>
                    <div className='relative w-full min-w-[175px]'>
                        <input
                            type='text'
                            name='campaign'
                            className='w-full rounded border border-stroke p-[6px]  px-3 py-2  outline-none focus:border-primary'
                            placeholder='Campaign'
                            onChange={(e) => filterCampaigns(e.target.value)}
                            value={campaignName}
                        />
                        <FaSearch
                            color='gray'
                            className='absolute right-3 top-1/2 -translate-y-1/2'
                        />
                    </div>
                    <input
                        type='date'
                        name='start_date'
                        className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                        value={dateRange.start_date}
                        onChange={(event) =>
                            setDateRange({
                                ...dateRange,
                                start_date: event.target.value,
                            })
                        }
                    />
                    to
                    <input
                        type='date'
                        name='end_date'
                        className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                        value={dateRange.end_date}
                        onChange={(event) =>
                            setDateRange({
                                ...dateRange,
                                end_date: event.target.value,
                            })
                        }
                    />
                    <div className='flex items-center gap-3'>
                        <input
                            type='checkbox'
                            name='campaign'
                            className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            placeholder='Campaign'
                            checked={only_todos}
                            onChange={handleOnlyShowChecked}
                            disabled={isLoading}
                        />{' '}
                        <label className='whitespace-nowrap'>
                            Only show open ToDos
                        </label>
                    </div>
                    <div className='flex items-center gap-3'>
                        <input
                            type='checkbox'
                            className='rounded border border-stroke p-[6px] px-3 py-2 outline-none focus:border-primary'
                            checked={hideDeleted}
                            onChange={(e) => setHideDeleted(e.target.checked)}
                            disabled={isLoading}
                        />{' '}
                        <label className='whitespace-nowrap'>
                            Hide Deleted
                        </label>
                    </div>
                    <AddMessageModal
                        type={'GENERAL' as any}
                        setNotes={refetch}
                    />
                </div>

                {(deptMessages ||
                    (state?.todo && only_todos) ||
                    myMessages) && (
                    <div className='bg-blue-50 mx-6 mb-3 flex items-center gap-2 rounded px-4 py-2 text-sm'>
                        <span className='font-medium'>Active filter:</span>
                        {deptMessages && (
                            <span className='rounded bg-[#952d46] px-2 py-0.5 text-white'>
                                Dept Messages -{' '}
                                {assignee?.label || deptMessagesDepartment}
                            </span>
                        )}
                        {state?.todo && only_todos && !deptMessages && (
                            <span className='rounded bg-[#00aeb3] px-2 py-0.5 text-white'>
                                Open To Dos
                            </span>
                        )}
                        {myMessages && !deptMessages && (
                            <span className='rounded bg-[#FEBF3E] px-2 py-0.5 text-white'>
                                My Messages
                            </span>
                        )}
                        <button
                            onClick={clearDashboardFilter}
                            className='ml-auto text-xs text-gray-500 underline hover:text-gray-700'
                        >
                            Clear filter
                        </button>
                    </div>
                )}

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>
                        Showing{' '}
                        {Math.min(
                            pageSize,
                            (data?.count || 0) - (page - 1) * pageSize,
                        )}{' '}
                        of {data?.count || 0} results
                    </p>

                    <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <BsPin className='text-primary' size={18} />
                            Pinned
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <LuListTodo className='text-primary' size={18} />
                            To Do
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <MdOutlineStickyNote2
                                className='text-primary'
                                size={18}
                            />
                            Note
                        </div>

                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <IoCheckmarkOutline
                                className='text-primary'
                                size={18}
                            />
                            Completed
                        </div>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <FiEye className='text-primary' size={18} />
                            View
                        </div>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <RiDeleteBinLine className='text-red' size={18} />
                            Delete
                        </div>
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table message-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                            {...(header.column.id === 'message'
                                                ? { className: 'message' }
                                                : null)}
                                            {...(header.column.id ===
                                                'isPinned' ||
                                            header.column.id === 'type'
                                                ? {
                                                      className:
                                                          'icon-eight-pixel',
                                                  }
                                                : null)}
                                            {...(header.column.id === 'campaign'
                                                ? {
                                                      className: 'campaign',
                                                  }
                                                : null)}
                                            key={header.id}
                                        >
                                            <div className='flex items-center'>
                                                <span
                                                    onClick={() =>
                                                        handleSort(
                                                            header.column.id,
                                                        )
                                                    }
                                                    className='cursor-pointer'
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column
                                                                  .columnDef
                                                                  .header,
                                                              header.getContext(),
                                                          )}
                                                </span>
                                            </div>
                                        </th>
                                    ))}
                                    <th className='icon-eight-pixel'>{''}</th>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading || isRefetching
                                ? Array.from({ length: 10 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                          <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                              <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                  <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => {
                                      return (
                                          <tr
                                              className={`border-t border-stroke ${
                                                  row
                                                      .getAllCells()
                                                      .find(
                                                          (c) =>
                                                              c.column.id ===
                                                                  'isPinned' &&
                                                              c.getValue(),
                                                      )
                                                      ? 'bg-[#fdeaea] hover:bg-[#fbdada]'
                                                      : 'hover:bg-[#f5f5f5]'
                                              }`}
                                              key={row.id}
                                          >
                                              {row
                                                  .getVisibleCells()
                                                  .map((cell) => (
                                                      <td
                                                          style={{
                                                              paddingTop:
                                                                  '16px',
                                                              paddingBottom:
                                                                  '16px',
                                                          }}
                                                          {...(cell.column
                                                              .id === 'message'
                                                              ? {
                                                                    className:
                                                                        'message',
                                                                }
                                                              : null)}
                                                          {...(cell.column
                                                              .id ===
                                                              'isPinned' ||
                                                          cell.column.id ===
                                                              'type'
                                                              ? {
                                                                    className:
                                                                        'icon-eight-pixel',
                                                                }
                                                              : null)}
                                                          key={cell.id}
                                                      >
                                                          <div className='flex items-center'>
                                                              {flexRender(
                                                                  cell.column
                                                                      .columnDef
                                                                      .cell,
                                                                  cell.getContext(),
                                                              )}
                                                          </div>
                                                      </td>
                                                  ))}
                                              <td className='icon-eight-pixel'>
                                                  <div className='flex items-center gap-3'>
                                                      <ViewMessageModal
                                                          message={row.original}
                                                          onDelete={
                                                              handleDelete
                                                          }
                                                          updateMessage={
                                                              handleUpdateMessage
                                                          }
                                                      />
                                                      <DeleteMessageModal
                                                          onDelete={
                                                              handleDelete
                                                          }
                                                          id={row.original.id}
                                                          campaignRequestId={
                                                              row.original
                                                                  .campaign_request_id
                                                          }
                                                      />
                                                  </div>
                                              </td>
                                          </tr>
                                      );
                                  })}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={page}
                    setPage={setPage}
                    pages={pages}
                    totalCount={data?.count}
                />
            </section>
        </>
    );
};

export default Messages;
